
import { EntityKind, type ConnectionStatus } from "../net/connection";
import { healthFraction } from "./health";
import type { DuelState } from "../net/connection";

/** The selected entity, as the panel needs it. Flattened from `RemoteEntity` so
 *  the HUD cannot accidentally depend on anything else about it. */
export interface TargetView {
  readonly id: number;
  readonly name: string;
  readonly kind: number;
  readonly level: number;
  /** The percentage the server allowed this client to see. Never reconstructed
   *  from the entity snapshot: that snapshot contains a player's real health,
   *  while `HEADER_GC_TARGET` deliberately reports zero for a player
   *  (`char.cpp:4926`, `PythonNetworkStreamPhaseGame.cpp:2383-2388`). */
  readonly hpPercent: number;
  /** Players have a name-and-actions target board but no health gauge in the
   *  original (`PythonNetworkStreamPhaseGame.cpp:2383-2388`). */
  readonly showHealth: boolean;
  /** The duel this client has with the selected player, if any. Drives which
   *  of the three legacy duel buttons the board shows, and whether it shows
   *  any buttons at all (`uitarget.py:387-392,423-429`). */
  readonly duelState: DuelState;
  readonly gradeName?: string;
  /** The social facts `RefreshButton` reads before it decides which buttons
   *  exist (`uitarget.py:378-441`). Absent means "this client has not been
   *  told", and every gate below then falls to the branch the original takes
   *  for a stranger. */
  readonly social?: TargetSocial;
}

export interface TargetSocial {
  /** `messenger.IsFriendByName(self.nameString)` (`uitarget.py:401`). FRIEND
   *  is shown only when the answer is no. */
  readonly friend?: boolean;
  /** `guild.MainPlayerHasAuthority(guild.AUTH_ADD_MEMBER)` and, with it,
   *  `not guild.IsMemberByName(name)` and `0 == chr.GetGuildID(vid)`
   *  (`uitarget.py:398-400`) -- all three, because the original shows the
   *  invite only when all three hold. */
  readonly mayInviteToGuild?: boolean;
  /** `player.IsPartyMember(self.vid)` (`uitarget.py:404`): the target is in
   *  *this client's* party. */
  readonly inMyParty?: boolean;
  /** `player.IsPartyLeader(self.vid)` (`uitarget.py:408`). */
  readonly targetLeadsMyParty?: boolean;
  /** `player.IsPartyMember(player.GetMainCharacterIndex())` (`uitarget.py:415`). */
  readonly selfInParty?: boolean;
  /** `player.IsPartyLeader(player.GetMainCharacterIndex())`
   *  (`uitarget.py:410,416`). */
  readonly selfLeadsParty?: boolean;
  readonly targetInParty?: boolean;
  /** `player.IsSameEmpire(self.vid)` (`uitarget.py:432`). False hides the
   *  party invite, the friend button and the duel. */
  readonly sameEmpire?: boolean;
  /** `player.GetCharacterDistance(self.vid)`, in mm. Past
   *  `EXCHANGE_LIMIT_RANGE` the trade button goes (`uitarget.py:437-440`). */
  readonly distanceMm?: number;
}

/** `TargetBoard.EXCHANGE_LIMIT_RANGE = 3000` (`uitarget.py:43`), in legacy
 *  units; one legacy unit is 10 mm. */
export const TARGET_EXCHANGE_LIMIT_MM = 30_000;

/** One target-board button: the visible label and the identity of the legacy
 *  control it is (`TARGET_BUTTON_*`, `uitarget.py:17-33`). */
export interface TargetButton {
  readonly id: TargetButtonId;
  readonly label: string;
}

export type TargetButtonId =
  | "WHISPER"
  | "EXCHANGE"
  | "FIGHT"
  | "ACCEPT_FIGHT"
  | "AVENGE"
  | "EMOTION_ALLOW"
  | "INVITE_GUILD"
  | "FRIEND"
  | "LEAVE_PARTY"
  | "EXCLUDE"
  | "INVITE_PARTY"
  | "REQUEST_ENTER_PARTY";

/** The shipped English `TARGET_BUTTON_*` strings
 *  (`locale_game.txt:533-548`). `ACCEPT_FIGHT` is "Agree" and `AVENGE` is
 *  "Revenge" -- both are in this tree's resource after all, at `:533-534`. */
export const TARGET_BUTTON_LABEL: Readonly<Record<TargetButtonId, string>> = {
  WHISPER: "Whispering",
  EXCHANGE: "Trading",
  FIGHT: "Duel",
  ACCEPT_FIGHT: "Agree",
  AVENGE: "Revenge",
  EMOTION_ALLOW: "Emotions",
  INVITE_GUILD: "Guild Invitation",
  FRIEND: "Friend",
  LEAVE_PARTY: "Leave Group",
  EXCLUDE: "Exclude",
  INVITE_PARTY: "Group Invitation",
  REQUEST_ENTER_PARTY: "Join Group",
};

export function targetBoardButtons(target: TargetView): TargetButton[] {
  const ids: TargetButtonId[] = [];
  const show = (id: TargetButtonId): void => {
    if (!ids.includes(id)) ids.push(id);
  };
  const hide = (id: TargetButtonId): void => {
    const at = ids.indexOf(id);
    if (at >= 0) ids.splice(at, 1);
  };

  // `player.IsPVPInstance(self.vid) or player.IsObserverMode()` returns before
  // `ShowDefaultButton` (`:385-392`): during a duel the board is a scoreboard.
  if (target.duelState === "fighting") return [];
  if (target.kind !== EntityKind.PLAYER || target.id === 0) return [];

  const social = target.social ?? {};

  // ShowDefaultButton (`:287-295`).
  show("WHISPER");
  show("EXCHANGE");
  show("FIGHT");
  show("EMOTION_ALLOW");

  // `:396-400`.
  if (social.mayInviteToGuild === true) show("INVITE_GUILD");
  // `:401-402`. Not-a-friend is the default: the original asks the messenger,
  // which answers no for everybody it has never heard of.
  if (social.friend !== true) show("FRIEND");

  if (social.inMyParty === true) {
    // `:404-411`.
    hide("FIGHT");
    if (social.targetLeadsMyParty === true) show("LEAVE_PARTY");
    else if (social.selfLeadsParty === true) show("EXCLUDE");
  } else {
    // `:413-421`.
    if (social.selfInParty === true) {
      if (social.selfLeadsParty === true) show("INVITE_PARTY");
    } else if (social.targetInParty === true) {
      show("REQUEST_ENTER_PARTY");
    } else {
      show("INVITE_PARTY");
    }

    // `:423-430`. Exactly one of the three duel controls, and `waiting`
    // (`IsCantFightInstance`) shows none of them.
    if (target.duelState === "revenge") {
      hide("FIGHT");
      show("AVENGE");
    } else if (target.duelState === "challenged") {
      hide("FIGHT");
      show("ACCEPT_FIGHT");
    } else if (target.duelState === "waiting") {
      hide("FIGHT");
    }

    // `:432-435`.
    if (social.sameEmpire === false) {
      hide("INVITE_PARTY");
      hide("FRIEND");
      hide("FIGHT");
    }
  }

  // `:437-440`.
  if (social.distanceMm !== undefined && social.distanceMm > TARGET_EXCHANGE_LIMIT_MM) {
    hide("EXCHANGE");
  }

  return ids.map((id) => ({ id, label: TARGET_BUTTON_LABEL[id] }));
}

/** The part of `Targeted` the board is permitted to read. Kept structural so
 *  this renderer does not need the generated message class. */
export interface TargetConfirmation {
  readonly target: number;
  readonly hp_percent: number;
}

export interface TargetActions {
  /** Escape closes the visible board and clears the selected target. */
  onClearTarget?: () => void;
  onWhisper?: (name: string) => void;
  onExchange?: (id: number) => void;
  onFriend?: (name: string) => void;
  onPartyInvite?: (id: number) => void;
  /** Ask to be let into the party the confirmed player is in. The other
   *  direction of `onPartyInvite`, and the original's own separate button
   *  (`uitarget.py:114,358-359`: `net.SendChatPacket("/party_request " +
   *  str(self.vid))`). */
  onPartyRequest?: (id: number) => void;
  /** Ask the server to invite the confirmed player to this guild. */
  onGuildInvite?: (id: number) => void;
  /** No id on the wire: this button asks to inspect the server-confirmed
   * selection that already caused this board to exist. */
  onInspectEquipment?: (id: number) => void;
  /** `TargetBoard.OnPartyExit` -- LEAVE_PARTY, shown on the *leader's* board
   *  when you are in their party (`uitarget.py:106,340-341`). It takes no id:
   *  a member may only remove themselves. */
  onPartyLeave?: () => void;
  /** `TargetBoard.OnPartyRemove` -- EXCLUDE, shown to the leader on a member's
   *  board (`uitarget.py:107,343-344`). */
  onPartyKick?: (id: number) => void;
  /** Challenge, accept or avenge -- all three legacy buttons are the same
   *  `OnPVP` call (`uitarget.py:101-103,331-332`), which sends nothing but
   *  `/pvp <vid>`.  The server decides which of the three it just was. */
  onDuel?: (id: number) => void;
  /** `TargetBoard.__OnEmotionAllow` (`uitarget.py:364-365`):
   *  `net.SendChatPacket("/emotion_allow %d" % (self.vid))`. Nothing else --
   *  the board says who, and the server decides what that permission means. */
  onEmotionAllow?: (id: number) => void;
}

/** The native `TargetBoard` starts at screen centre, y=10, with a 250x40
 *  name board (`uitarget.py:265-280`). Keep these landmarks explicit so the
 *  browser panel cannot drift back into the overlay's top-left flow. */
export const LEGACY_TARGET_BOARD_GEOMETRY = {
  top: 10,
  width: 250,
  height: 40,
} as const;

/** The non-Arabic legacy button is right-aligned with a 30px inset at (y=13)
 * (`uitarget.py:61-73`); the bundled close-button visual is 21px square. */
export const LEGACY_TARGET_CLOSE_GEOMETRY = {
  top: 13,
  right: 30,
  size: 21,
} as const;

/** `TargetBoard` builds its legacy HP gauge with a fixed 130px width
 * (`uitarget.py:49-52`), independent of the name board's expanded width. */
export const LEGACY_TARGET_HP_GAUGE_WIDTH = 130;
/** Legacy `MakeGauge(130, "red")` skin (`uitarget.py:49-52`). */
export const LEGACY_TARGET_HP_GAUGE_COLOUR = "#d94f4f";

/** Width used by the legacy board when its monster HP gauge is shown.
 * `TargetBoard.SetHP` expands to `200 + 7 * nameLength`
 * (`uitarget.py:265-283`); player boards keep the 250px reset width. */
export function legacyTargetBoardWidth(name: string, showHealth: boolean): number {
  return showHealth ? 200 + 7 * name.length : LEGACY_TARGET_BOARD_GEOMETRY.width;
}

export function duelButtonLabel(state: DuelState): string {
  switch (state) {
    case "revenge":
      return TARGET_BUTTON_LABEL.AVENGE;
    case "challenged":
      return TARGET_BUTTON_LABEL.ACCEPT_FIGHT;
    default:
      return TARGET_BUTTON_LABEL.FIGHT;
  }
}

export function legacyTargetBoardName(
  level: number,
  gradeName: string | undefined,
  name: string,
): string {
  let front = "";
  if (level !== -1 && level >= 0) front += `Lv.${level} `;
  if (gradeName !== undefined && gradeName !== "") front += `(${gradeName}) `;
  return front + name;
}

export function confirmedTargetView(
  confirmed: TargetConfirmation,
  entity: {
    readonly id: number;
    readonly name: string;
    readonly kind: number;
    readonly level: number;
  } | null,
  duelState: DuelState = "none",
  social?: TargetSocial,
): TargetView | null {
  if (
    confirmed.target === 0 ||
    entity === null ||
    entity.id !== confirmed.target
  ) {
    return null;
  }
  return {
    id: entity.id,
    name: entity.name,
    kind: entity.kind,
    level: entity.level,
    hpPercent: Math.max(0, Math.min(100, confirmed.hp_percent)),
    showHealth: entity.kind !== EntityKind.PLAYER,
    duelState,
    social,
  };
}

export interface HudModel {
  status: ConnectionStatus;
  detail: string;
  pingMs: number;
  tick: number;
  entities: number;
  errorMm: number;
  unacked: number;
  mispredictionRate: number;
  fps: number;
  xMm: number;
  yMm: number;
  /** Null when nothing is selected, which is also when the panel disappears
   *  entirely rather than sitting there empty. */
  target: TargetView | null;
  playerLevel: number;
}

const ROWS = [
  "status",
  "ping",
  "tick",
  "entities",
  "error",
  "unacked",
  "position",
  "fps",
] as const;

type Row = (typeof ROWS)[number];

export class Hud {
  private readonly values = new Map<Row, HTMLElement>();
  private readonly root: HTMLElement;
  private readonly targetPanel: HTMLElement;
  private readonly targetName: HTMLElement;
  private readonly targetBar: HTMLElement;
  private readonly targetFill: HTMLElement;
  private readonly targetActions: HTMLElement;
  private readonly targetClose: HTMLButtonElement;
  private last = "";
  private actions: TargetActions = {};
  private targetBoardVisible = true;
  private lastTarget: TargetView | null = null;

  constructor(container: HTMLElement, diagnostics = false, actions: TargetActions = {}) {
    this.actions = actions;
    this.root = document.createElement("div");
    this.root.className = "hud";
    if (diagnostics) {
      const heading = document.createElement("div");
      heading.className = "hud-diagnostics-title";
      heading.textContent = "Diagnostics";
      this.root.append(heading);
    }
    for (const row of ROWS) {
      const line = document.createElement("div");
      line.className = "hud-row";

      const label = document.createElement("span");
      label.className = "hud-label";
      label.textContent = row;

      const value = document.createElement("span");
      value.className = "hud-value";
      value.textContent = "-";

      line.append(label, value);
      this.root.append(line);
      this.values.set(row, value);
    }
    if (diagnostics) container.append(this.root);

    this.targetPanel = document.createElement("div");
    this.targetPanel.className = "hud panel-target";
    this.targetPanel.hidden = true;
    // `TargetBoard.UpdatePosition` uses screen-width/2 minus half the board
    // width (`uitarget.py:225-226`). Inline landmarks keep this true even in a
    // test DOM that has no imported style sheet, while style.css handles the
    // mobile width override and the surrounding legacy finish.
    this.targetPanel.style.position = "fixed";
    this.targetPanel.style.top = `${LEGACY_TARGET_BOARD_GEOMETRY.top}px`;
    this.targetPanel.style.left = "50%";
    this.targetPanel.style.width = `${LEGACY_TARGET_BOARD_GEOMETRY.width}px`;
    this.targetPanel.style.maxWidth = "calc(100vw - 24px)";
    this.targetPanel.style.minHeight = `${LEGACY_TARGET_BOARD_GEOMETRY.height}px`;
    this.targetPanel.style.transform = "translateX(-50%)";
    // #overlay deliberately does not catch gameplay taps. A target board is a
    // native control surface, so its own buttons must opt back in.
    this.targetPanel.style.pointerEvents = "auto";

    this.targetName = document.createElement("div");
    this.targetName.className = "target-name";
    this.targetBar = document.createElement("div");
    this.targetBar.className = "target-bar";
    this.targetBar.style.width = `${LEGACY_TARGET_HP_GAUGE_WIDTH}px`;
    this.targetFill = document.createElement("div");
    this.targetFill.className = "target-bar-fill";
    this.targetFill.style.background = LEGACY_TARGET_HP_GAUGE_COLOUR;
    this.targetBar.append(this.targetFill);

    this.targetActions = document.createElement("div");
    this.targetActions.className = "target-actions";

    this.targetClose = document.createElement("button");
    this.targetClose.type = "button";
    this.targetClose.className = "target-close";
    this.targetClose.textContent = "×";
    this.targetClose.setAttribute("aria-label", "Close target");
    this.targetClose.style.top = `${LEGACY_TARGET_CLOSE_GEOMETRY.top}px`;
    this.targetClose.style.right = `${LEGACY_TARGET_CLOSE_GEOMETRY.right}px`;
    this.targetClose.style.width = `${LEGACY_TARGET_CLOSE_GEOMETRY.size}px`;
    this.targetClose.style.height = `${LEGACY_TARGET_CLOSE_GEOMETRY.size}px`;
    // `TargetBoard.OnPressedCloseButton` clears the target before hiding it
    // (`uitarget.py:152-157`). Keep the same local close affordance; the next
    // server confirmation remains authoritative about any new selection.
    this.targetClose.addEventListener("click", () => {
      if (this.targetPanel.hidden || this.lastTarget === null) return;
      this.lastTarget = null;
      this.targetPanel.hidden = true;
      this.actions.onClearTarget?.();
    });

    this.targetPanel.append(
      this.targetClose,
      this.targetName,
      this.targetBar,
      this.targetActions,
    );
    this.targetPanel.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || this.targetPanel.hidden || this.lastTarget === null) return;
      event.preventDefault();
      event.stopPropagation();
      this.lastTarget = null;
      this.targetPanel.hidden = true;
      this.actions.onClearTarget?.();
    });
    container.append(this.targetPanel);

  }

  /** Replace callbacks without changing what the board knows about its target.
   * The session and chat box are constructed after the HUD, so this keeps that
   * setup order while avoiding a parallel, stale selected-entity cache. */
  setTargetActions(actions: TargetActions): void {
    this.actions = actions;
  }

  /** Apply the local display preference immediately, including to a board that
   * is already rendered. The server-owned target itself is not cleared: turning
   * the preference back on should reveal the last confirmed target again. */
  setTargetBoardVisible(visible: boolean): void {
    this.targetBoardVisible = visible;
    this.targetPanel.hidden = !visible || this.lastTarget === null;
  }

  setConfirmedTarget(target: TargetView | null): void {
    this.updateTarget(target);
  }

  update(model: HudModel): void {
    // The HUD is written once per frame but read by a human at maybe 4 Hz; a
    // string compare is far cheaper than eight DOM writes that change nothing.
    const fingerprint = JSON.stringify(model);
    if (fingerprint === this.last) return;
    this.last = fingerprint;

    // Written whether or not the panel is in the document. The rows are cheap,
    // the fingerprint above already skips the common case, and a readout that
    // only starts recording once someone asks for it is a readout that has
    // nothing to say about the frame in which the problem happened.
    this.set("status", `${model.status} — ${model.detail}`);
    this.set("ping", model.pingMs < 0 ? "—" : `${model.pingMs} ms`);
    this.set("tick", String(model.tick));
    this.set("entities", String(model.entities));
    this.set(
      "error",
      `${model.errorMm} mm (${(model.mispredictionRate * 100).toFixed(1)}% miss)`,
    );
    this.set("unacked", String(model.unacked));
    this.set(
      "position",
      `${(model.xMm / 1000).toFixed(1)}, ${(model.yMm / 1000).toFixed(1)} m`,
    );
    this.set("fps", model.fps.toFixed(0));

    this.updateTarget(model.target);
  }

  private updateTarget(target: TargetView | null): void {
    this.lastTarget = target;
    this.targetPanel.hidden = !this.targetBoardVisible || target === null;
    if (target === null) return;

    const bareName = target.name === "" ? `#${target.id}` : target.name;
    this.targetName.textContent = target.showHealth
      ? legacyTargetBoardName(target.level, target.gradeName, bareName)
      : bareName;
    this.targetPanel.style.width = `${legacyTargetBoardWidth(this.targetName.textContent, target.showHealth)}px`;

    this.updateTargetActions(target);

    // A player target has no gauge in the original. In particular, zero is not
    // an empty player bar: it is the server withholding the value
    // (`char.cpp:4926`, `PythonNetworkStreamPhaseGame.cpp:2383-2388`).
    this.targetBar.hidden = !target.showHealth;
    if (!target.showHealth) {
      this.targetFill.style.width = "0%";
      return;
    }
    const fraction = healthFraction(target.hpPercent, 100);
    this.targetFill.style.width = `${(fraction * 100).toFixed(1)}%`;
  }

  private updateTargetActions(target: TargetView): void {
    // Which buttons exist is `RefreshButton`'s decision and lives in
    // `targetBoardButtons`, which is a port of it. This method only turns each
    // one into a control: the handler per legacy button id, and the refusal to
    // send a name-addressed request with no name.
    const usableName = target.name !== "";
    const handlers: Readonly<Record<TargetButtonId, (() => void) | undefined>> = {
      WHISPER: usableName ? () => this.actions.onWhisper?.(target.name) : undefined,
      EXCHANGE: () => this.actions.onExchange?.(target.id),
      // All three duel controls are one call (`uitarget.py:101-103,331-332`):
      // `/pvp <vid>`, and the server decides which of the three it just was.
      FIGHT: () => this.actions.onDuel?.(target.id),
      ACCEPT_FIGHT: () => this.actions.onDuel?.(target.id),
      AVENGE: () => this.actions.onDuel?.(target.id),
      EMOTION_ALLOW: () => this.actions.onEmotionAllow?.(target.id),
      INVITE_GUILD: () => this.actions.onGuildInvite?.(target.id),
      FRIEND: usableName ? () => this.actions.onFriend?.(target.name) : undefined,
      LEAVE_PARTY: () => this.actions.onPartyLeave?.(),
      EXCLUDE: () => this.actions.onPartyKick?.(target.id),
      INVITE_PARTY: () => this.actions.onPartyInvite?.(target.id),
      REQUEST_ENTER_PARTY: () => this.actions.onPartyRequest?.(target.id),
    };
    this.targetActions.replaceChildren(
      ...targetBoardButtons(target).flatMap((entry) => {
        const action = handlers[entry.id];
        if (action === undefined) return [];
        const button = document.createElement("button");
        button.type = "button";
        button.className = "target-action";
        button.dataset.targetButton = entry.id;
        button.textContent = entry.label;
        button.addEventListener("click", action);
        return [button];
      }),
    );
    this.targetActions.hidden = this.targetActions.childElementCount === 0;
  }

  private set(row: Row, text: string): void {
    const element = this.values.get(row);
    if (element !== undefined && element.textContent !== text) {
      element.textContent = text;
    }
  }
}

/** Rolling frame rate. A single frame's delta is noise; the average is readable. */
export class FrameCounter {
  private samples: number[] = [];

  push(dtMs: number): void {
    if (dtMs <= 0) return;
    this.samples.push(dtMs);
    if (this.samples.length > 60) this.samples.shift();
  }

  get fps(): number {
    if (this.samples.length === 0) return 0;
    const mean = this.samples.reduce((a, b) => a + b, 0) / this.samples.length;
    return 1000 / mean;
  }
}
